import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import {
  fetchTransactionListPeriodReportAction,
  setError,
} from "../slices/transactionListReportSlice";
import {
  addMonths,
  endOfMonth,
  format,
  startOfMonth,
  subMonths,
} from "date-fns";

const MONTHS_AVAILABLE = 18;

interface SelectOption {
  value: string;
  label: string;
}

const monthKey = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, "0")}`;

const parseKey = (key: string): { year: number; month: number } => {
  const [year, month] = key.split("-").map(Number);
  return { year, month };
};

export const useTransactionListReportBuilder = () => {
  const dispatch = useDispatch();

  const report = useSelector(
    (state: RootState) => state.transactionListReport.report,
  );
  const loading = useSelector(
    (state: RootState) => state.transactionListReport.loading,
  );
  const error = useSelector(
    (state: RootState) => state.transactionListReport.error,
  );

  const { earliestMonth, latestMonth, defaultKey } = useMemo(() => {
    const latest = startOfMonth(new Date());
    const earliest = subMonths(latest, MONTHS_AVAILABLE - 1);
    const defaultM = subMonths(latest, 1);
    return {
      earliestMonth: earliest,
      latestMonth: latest,
      defaultKey: monthKey(defaultM.getFullYear(), defaultM.getMonth() + 1),
    };
  }, []);

  const [startKey, setStartKey] = useState<string>(defaultKey);
  const [endKey, setEndKey] = useState<string>(defaultKey);

  const monthOptions = useMemo<SelectOption[]>(() => {
    const opts: SelectOption[] = [];
    let cursor = earliestMonth;
    while (cursor <= latestMonth) {
      opts.push({
        value: monthKey(cursor.getFullYear(), cursor.getMonth() + 1),
        label: format(cursor, "MMMM yyyy"),
      });
      cursor = addMonths(cursor, 1);
    }
    return opts;
  }, [earliestMonth, latestMonth]);

  const startMonthOptions = monthOptions;

  const endMonthOptions = useMemo<SelectOption[]>(
    () => monthOptions.filter((o) => o.value >= startKey),
    [monthOptions, startKey],
  );

  // Push end forward if it falls behind start
  useEffect(() => {
    if (endKey < startKey) {
      setEndKey(startKey);
    }
  }, [startKey, endKey]);

  const handleStartKeyChange = useCallback(
    (key: string) => {
      setStartKey(key);
      dispatch(setError(null));
    },
    [dispatch],
  );

  const handleEndKeyChange = useCallback(
    (key: string) => {
      setEndKey(key);
      dispatch(setError(null));
    },
    [dispatch],
  );

  const resolveBoundaries = useCallback(() => {
    const { year: sY, month: sM } = parseKey(startKey);
    const { year: eY, month: eM } = parseKey(endKey);
    const today = new Date();
    const isCurrentMonth =
      eY === today.getFullYear() && eM === today.getMonth() + 1;
    const start = new Date(sY, sM - 1, 1);
    const end = isCurrentMonth ? today : endOfMonth(new Date(eY, eM - 1, 1));
    return { start, end };
  }, [startKey, endKey]);

  const resolvedRange = useMemo(() => {
    const { start, end } = resolveBoundaries();
    return `${format(start, "MMM d, yyyy")} - ${format(end, "MMM d, yyyy")}`;
  }, [resolveBoundaries]);

  const handleGenerateReport = useCallback(() => {
    const { start, end } = resolveBoundaries();
    dispatch(
      fetchTransactionListPeriodReportAction({
        periodStart: format(start, "yyyy-MM-dd"),
        periodEnd: format(end, "yyyy-MM-dd"),
      }),
    );
  }, [dispatch, resolveBoundaries]);

  const canGenerate = startKey <= endKey;

  return {
    startKey,
    endKey,
    setStartKey: handleStartKeyChange,
    setEndKey: handleEndKeyChange,
    startMonthOptions,
    endMonthOptions,
    resolvedRange,
    report,
    loading,
    error,
    handleGenerateReport,
    canGenerate,
  };
};
